import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { CircularProgress } from "@mui/material";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiButton from "components/SuiButton";
import ControlledInputField from "components/ControlledInputField";
import ErrorAlert from "components/ErrorAlert";
import CoverLayout from "layouts/authentication/components/CoverLayout";
import { signIn } from "layouts/authentication/auth.thunk";
import { signInSchema } from "layouts/authentication/sign-in/sign-in.constants";

import curved9 from "assets/images/haazri-logo.jpg";

// OAuth 2.1 + PKCE authorization endpoint (RFC 8414's `authorization_endpoint`
// for haazri-api's authorization server — see that repo's controllers/oauth.js).
// A remote MCP client (e.g. Claude) redirects here with these query params;
// this page logs the admin in (if needed), gets their explicit consent, then
// redirects back to `redirect_uri` with a short-lived authorization code.
// Deliberately not wrapped in PrivateRoute/PublicRoute (see routes.js) — both
// would drop the query string on their redirect, which would lose the whole
// OAuth request.
function OauthAuthorize() {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState(null);

  const {
    formState: { errors },
    control,
    handleSubmit,
  } = useForm({ resolver: yupResolver(signInSchema) });

  const { loading, error, isLoggedIn, token, admin } = useSelector((state) => state.auth);

  const client_id = searchParams.get("client_id");
  const redirect_uri = searchParams.get("redirect_uri");
  const state = searchParams.get("state");
  const code_challenge = searchParams.get("code_challenge");
  const code_challenge_method = searchParams.get("code_challenge_method") || "S256";
  const client_name = searchParams.get("client_name");

  const missingParams = !client_id || !redirect_uri || !code_challenge;

  function onSignIn(data) {
    dispatch(signIn(data));
  }

  async function onAllow() {
    setApproveError(null);
    setApproving(true);
    try {
      // Authorization header is attached automatically by the axios
      // interceptor in services/apiConfig (same as every other admin-portal
      // API call) once state.auth.token is set.
      const { data } = await axios.post("/oauth/authorize/approve", {
        client_id,
        redirect_uri,
        code_challenge,
        code_challenge_method,
      });
      window.location.href = `${redirect_uri}?code=${encodeURIComponent(data.code)}${
        state ? `&state=${encodeURIComponent(state)}` : ""
      }`;
    } catch (err) {
      setApproving(false);
      setApproveError(
        err.response?.data?.error_description || err.response?.data?.error || "Something went wrong"
      );
    }
  }

  function onDeny() {
    window.location.href = `${redirect_uri}?error=access_denied${
      state ? `&state=${encodeURIComponent(state)}` : ""
    }`;
  }

  if (missingParams) {
    return (
      <CoverLayout title="Authorize" image={curved9}>
        <SuiTypography variant="body2" color="text">
          This authorization link is missing required parameters. Please restart the connection
          from the app you were trying to connect.
        </SuiTypography>
      </CoverLayout>
    );
  }

  if (!isLoggedIn || !token) {
    return (
      <CoverLayout
        title="Haazri Admin Portal"
        description="Sign in to authorize access to your Haazri data"
        image={curved9}
      >
        {error && <ErrorAlert color="error" message={error} />}
        <SuiBox component="form" role="form" onSubmit={handleSubmit(onSignIn)}>
          <SuiBox>
            <SuiBox mb={1} ml={0.5}>
              <SuiTypography component="label" variant="caption" fontWeight="bold">
                Email
              </SuiTypography>
            </SuiBox>
            <ControlledInputField
              type="email"
              placeholder="Enter Email"
              name="email"
              control={control}
              errors={errors}
            />
          </SuiBox>
          <SuiBox mb={2}>
            <SuiBox mb={1} ml={0.5}>
              <SuiTypography component="label" variant="caption" fontWeight="bold">
                Password
              </SuiTypography>
            </SuiBox>
            <ControlledInputField
              type="password"
              placeholder="Enter Password"
              name="password"
              control={control}
              errors={errors}
            />
          </SuiBox>
          <SuiBox mt={4} mb={1}>
            <SuiButton variant="gradient" color="info" fullWidth type="submit" disabled={loading}>
              {loading ? <CircularProgress color="white" size={17} /> : "sign in"}
            </SuiButton>
          </SuiBox>
        </SuiBox>
      </CoverLayout>
    );
  }

  return (
    <CoverLayout title="Authorize access" image={curved9}>
      {approveError && <ErrorAlert color="error" message={approveError} />}
      <SuiTypography variant="body2" color="text">
        <SuiTypography variant="body2" fontWeight="bold" component="span">
          {client_name || "This app"}
        </SuiTypography>{" "}
        wants to access your Haazri attendance data as{" "}
        <SuiTypography variant="body2" fontWeight="bold" component="span">
          {admin?.email}
        </SuiTypography>
        .
      </SuiTypography>
      <SuiBox mt={4} mb={1}>
        <SuiButton variant="gradient" color="info" fullWidth disabled={approving} onClick={onAllow}>
          {approving ? <CircularProgress color="white" size={17} /> : "Allow"}
        </SuiButton>
      </SuiBox>
      <SuiBox mb={1}>
        <SuiButton variant="outlined" color="secondary" fullWidth disabled={approving} onClick={onDeny}>
          Deny
        </SuiButton>
      </SuiBox>
    </CoverLayout>
  );
}

export default OauthAuthorize;
